import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

// API URL - use Worker in dev, or deployed Worker URL in production
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:9001/api';

// Login Component
function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    // Simple validation - in production this would verify against backend
    if (email && password) {
      // Store user in localStorage
      const user = { email, name: email.split('@')[0] };
      localStorage.setItem('teap_user', JSON.stringify(user));
      onLogin(user);
    } else {
      setError('Please enter email and password');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>TEAP Manager</h1>
          <p>FIFA World Cup 2026 - Transport Team</p>
        </div>
        <form onSubmit={handleSubmit}>
          {error && <div className="login-error">{error}</div>}
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="your.email@fifa.org"
              autoFocus
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="Enter password"
            />
          </div>
          <button type="submit" className="btn-primary login-btn">
            Sign In
          </button>
        </form>
        <div className="login-footer">
          <p>TPT Technology Enablement Process</p>
        </div>
      </div>
    </div>
  );
}

// Navigation Component
function Navigation({ currentPage, setCurrentPage, user, onLogout }) {
  const pages = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'templates', label: 'Template Builder' },
    { id: 'planner', label: 'Venue Details' },
    { id: 'bundles', label: 'Bundles' },
    { id: 'compare', label: 'Compare Versions' },
    { id: 'procurement', label: 'Procurement' },
    { id: 'exports', label: 'Exports' },
    { id: 'versions', label: 'Versions' },
    { id: 'inventory', label: 'Baseline Data' }
  ];

  return (
    <nav className="nav">
      <div className="nav-brand">TEAP Manager</div>
      <div className="nav-links">
        {pages.map(page => (
          <button
            key={page.id}
            className={`nav-link ${currentPage === page.id ? 'active' : ''}`}
            onClick={() => setCurrentPage(page.id)}
          >
            {page.label}
          </button>
        ))}
      </div>
      <div className="nav-user">
        <span className="user-name">{user?.name || user?.email}</span>
        <button className="btn-logout" onClick={onLogout}>Logout</button>
      </div>
    </nav>
  );
}

// Dashboard Component
function Dashboard() {
  const [stats, setStats] = useState(null);
  const [requestSummary, setRequestSummary] = useState([]);
  const [productSummary, setProductSummary] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, reqSumRes, prodSumRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/stats`),
        axios.get(`${API_URL}/requests/summary`),
        axios.get(`${API_URL}/requests/by-product-type`)
      ]);
      setStats(statsRes.data);
      setRequestSummary(reqSumRes.data);
      setProductSummary(prodSumRes.data);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  if (!stats) return <div className="loading">Loading...</div>;

  return (
    <div className="dashboard">
      <h1>TEAP Dashboard</h1>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.venues}</div>
          <div className="stat-label">Venues</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.products}</div>
          <div className="stat-label">Products</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.requests.toLocaleString()}</div>
          <div className="stat-label">TEAP Requests</div>
        </div>
        <div className="stat-card highlight">
          <div className="stat-value">{stats.total_items.toLocaleString()}</div>
          <div className="stat-label">Total Items</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.bundles}</div>
          <div className="stat-label">Bundles</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.pricing_items}</div>
          <div className="stat-label">Pricing Items</div>
        </div>
      </div>

      <div className="dashboard-sections">
        <div className="section">
          <h2>Requests by Country</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>Requests</th>
                <th>Venues</th>
                <th>Items</th>
              </tr>
            </thead>
            <tbody>
              {requestSummary.map(row => (
                <tr key={row.country || 'unknown'}>
                  <td>{row.country || 'N/A'}</td>
                  <td>{parseInt(row.total_requests).toLocaleString()}</td>
                  <td>{row.venues}</td>
                  <td>{parseInt(row.total_items).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="section">
          <h2>Top Product Types</h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Product Type</th>
                <th>Requests</th>
                <th>Quantity</th>
              </tr>
            </thead>
            <tbody>
              {productSummary.slice(0, 10).map(row => (
                <tr key={row.product_type}>
                  <td>{row.product_type}</td>
                  <td>{parseInt(row.request_count).toLocaleString()}</td>
                  <td>{parseInt(row.total_quantity).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Inventory Component
function Inventory() {
  const [requests, setRequests] = useState([]);
  const [filters, setFilters] = useState({ country: '', venue_name: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.country) params.append('country', filters.country);
      if (filters.venue_name) params.append('venue_name', filters.venue_name);
      params.append('limit', '100');

      const res = await axios.get(`${API_URL}/requests?${params}`);
      setRequests(res.data);
    } catch (err) {
      console.error('Error fetching requests:', err);
    }
    setLoading(false);
  };

  const handleFilter = () => {
    fetchRequests();
  };

  return (
    <div className="inventory">
      <h1>Inventory Browser</h1>

      <div className="filters">
        <select
          value={filters.country}
          onChange={e => setFilters({ ...filters, country: e.target.value })}
        >
          <option value="">All Countries</option>
          <option value="USA">USA</option>
          <option value="MEX">Mexico</option>
          <option value="CAN">Canada</option>
        </select>
        <input
          type="text"
          placeholder="Search venue..."
          value={filters.venue_name}
          onChange={e => setFilters({ ...filters, venue_name: e.target.value })}
        />
        <button onClick={handleFilter}>Filter</button>
      </div>

      {loading ? (
        <div className="loading">Loading...</div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Venue</th>
              <th>Space</th>
              <th>Product</th>
              <th>Type</th>
              <th>Qty</th>
              <th>Country</th>
            </tr>
          </thead>
          <tbody>
            {requests.map(req => (
              <tr key={req.id}>
                <td>{req.venue_name}</td>
                <td>{req.space_name}</td>
                <td>{req.product_name}</td>
                <td>{req.product_type}</td>
                <td>{req.quantity}</td>
                <td>{req.country}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Bundles Component
function Bundles() {
  const [bundles, setBundles] = useState([]);
  const [products, setProducts] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [newBundle, setNewBundle] = useState({ bundle_name: '', bundle_type: 'Location', description: '' });
  const [newComponent, setNewComponent] = useState({ product_code: '', product_name: '', quantity: 1, is_on_menu: true });

  useEffect(() => {
    fetchBundles();
    fetchProducts();
    fetchPricing();
  }, []);

  const fetchBundles = async () => {
    try {
      const res = await axios.get(`${API_URL}/bundles`);
      setBundles(res.data);
    } catch (err) {
      console.error('Error fetching bundles:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API_URL}/products`);
      setProducts(res.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  const fetchPricing = async () => {
    try {
      const res = await axios.get(`${API_URL}/pricing`);
      setPricing(res.data);
    } catch (err) {
      console.error('Error fetching pricing:', err);
    }
  };

  const fetchBundleDetails = async (id) => {
    try {
      const res = await axios.get(`${API_URL}/bundles/${id}`);
      setSelectedBundle(res.data);
    } catch (err) {
      console.error('Error fetching bundle details:', err);
    }
  };

  const createBundle = async () => {
    try {
      const res = await axios.post(`${API_URL}/bundles`, newBundle);
      setShowForm(false);
      setNewBundle({ bundle_name: '', bundle_type: 'Location', description: '' });
      fetchBundles();
      fetchBundleDetails(res.data.id);
    } catch (err) {
      console.error('Error creating bundle:', err);
      alert('Error creating bundle: ' + err.message);
    }
  };

  const deleteBundle = async (id) => {
    if (!window.confirm('Delete this bundle?')) return;
    try {
      await axios.delete(`${API_URL}/bundles/${id}`);
      setSelectedBundle(null);
      fetchBundles();
    } catch (err) {
      console.error('Error deleting bundle:', err);
    }
  };

  const addComponent = async () => {
    if (!selectedBundle || !newComponent.product_name) return;
    try {
      await axios.post(`${API_URL}/bundles/${selectedBundle.id}/components`, newComponent);
      setNewComponent({ product_code: '', product_name: '', quantity: 1, is_on_menu: true });
      fetchBundleDetails(selectedBundle.id);
      fetchBundles();
    } catch (err) {
      console.error('Error adding component:', err);
    }
  };

  const removeComponent = async (componentId) => {
    if (!selectedBundle) return;
    try {
      await axios.delete(`${API_URL}/bundles/${selectedBundle.id}/components/${componentId}`);
      fetchBundleDetails(selectedBundle.id);
      fetchBundles();
    } catch (err) {
      console.error('Error removing component:', err);
    }
  };

  const handleProductSelect = (productCode) => {
    const product = products.find(p => p.product_code === productCode);
    if (product) {
      setNewComponent({
        ...newComponent,
        product_code: product.product_code,
        product_name: product.product_name,
        is_on_menu: true
      });
    }
  };

  const getItemPrice = (productName) => {
    const priceItem = pricing.find(p =>
      productName.toLowerCase().includes(p.item_type.toLowerCase()) ||
      p.item_type.toLowerCase().includes(productName.toLowerCase().split(' ')[0])
    );
    return priceItem?.unit_cost || 0;
  };

  const calculateBundleCost = () => {
    if (!selectedBundle?.components) return 0;
    return selectedBundle.components.reduce((total, comp) => {
      return total + (getItemPrice(comp.product_name) * (comp.quantity || 1));
    }, 0);
  };

  return (
    <div className="bundles">
      <div className="page-header">
        <h1>Bundle Manager</h1>
        <button className="btn-primary" onClick={() => { setShowForm(!showForm); setSelectedBundle(null); }}>
          {showForm ? 'Cancel' : '+ New Bundle'}
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create New Bundle</h3>
          <div className="form-row">
            <div className="form-group">
              <label>Bundle Name</label>
              <input
                type="text"
                value={newBundle.bundle_name}
                onChange={e => setNewBundle({ ...newBundle, bundle_name: e.target.value })}
                placeholder="e.g., Workstation Bundle"
              />
            </div>
            <div className="form-group">
              <label>Bundle Type</label>
              <select
                value={newBundle.bundle_type}
                onChange={e => setNewBundle({ ...newBundle, bundle_type: e.target.value })}
              >
                <option value="Location">Location Bundle</option>
                <option value="Device">Device Bundle</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={newBundle.description}
              onChange={e => setNewBundle({ ...newBundle, description: e.target.value })}
              placeholder="Bundle description..."
            />
          </div>
          <button className="btn-primary" onClick={createBundle}>Create Bundle</button>
        </div>
      )}

      <div className="bundles-layout">
        <div className="bundles-list">
          <h3>Bundles ({bundles.length})</h3>
          {bundles.length === 0 ? (
            <div className="empty-state">
              <p>No bundles created yet.</p>
            </div>
          ) : (
            bundles.map(bundle => (
              <div
                key={bundle.id}
                className={`bundle-card ${selectedBundle?.id === bundle.id ? 'selected' : ''}`}
                onClick={() => fetchBundleDetails(bundle.id)}
              >
                <div className="bundle-header">
                  <h4>{bundle.bundle_name}</h4>
                  <span className="bundle-type">{bundle.bundle_type}</span>
                </div>
                <p>{bundle.description}</p>
                <div className="bundle-meta">
                  <span>{bundle.component_count || 0} components</span>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="bundle-detail">
          {selectedBundle ? (
            <>
              <div className="detail-header">
                <div>
                  <h2>{selectedBundle.bundle_name}</h2>
                  <span className="bundle-type">{selectedBundle.bundle_type}</span>
                </div>
                <button className="btn-danger" onClick={() => deleteBundle(selectedBundle.id)}>Delete Bundle</button>
              </div>
              <p className="bundle-description">{selectedBundle.description}</p>

              <div className="components-section">
                <h3>Components</h3>

                <div className="add-component">
                  <select
                    value={newComponent.product_code}
                    onChange={e => handleProductSelect(e.target.value)}
                  >
                    <option value="">-- Select from IT Menu --</option>
                    {products.map(p => (
                      <option key={p.product_code} value={p.product_code}>
                        {p.product_code} - {p.product_name}
                      </option>
                    ))}
                  </select>
                  <span className="or-divider">or</span>
                  <input
                    type="text"
                    placeholder="Custom item name (off-menu)"
                    value={newComponent.is_on_menu ? '' : newComponent.product_name}
                    onChange={e => setNewComponent({
                      ...newComponent,
                      product_name: e.target.value,
                      product_code: '',
                      is_on_menu: false
                    })}
                  />
                  <input
                    type="number"
                    min="1"
                    value={newComponent.quantity}
                    onChange={e => setNewComponent({ ...newComponent, quantity: parseInt(e.target.value) || 1 })}
                    style={{ width: '60px' }}
                  />
                  <button className="btn-primary" onClick={addComponent}>Add</button>
                </div>

                {selectedBundle.components?.length > 0 ? (
                  <table className="data-table components-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Code</th>
                        <th>Qty</th>
                        <th>Unit Price</th>
                        <th>Total</th>
                        <th>Source</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBundle.components.map(comp => {
                        const unitPrice = getItemPrice(comp.product_name);
                        const totalPrice = unitPrice * (comp.quantity || 1);
                        return (
                          <tr key={comp.id}>
                            <td>{comp.product_name}</td>
                            <td>{comp.product_code || '-'}</td>
                            <td>{comp.quantity}</td>
                            <td>${unitPrice.toFixed(2)}</td>
                            <td>${totalPrice.toFixed(2)}</td>
                            <td>
                              <span className={`source-tag ${comp.is_on_menu ? 'on-menu' : 'off-menu'}`}>
                                {comp.is_on_menu ? 'IT Menu' : 'Off-Menu'}
                              </span>
                            </td>
                            <td>
                              <button className="btn-small btn-danger" onClick={() => removeComponent(comp.id)}>×</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4"><strong>Bundle Total</strong></td>
                        <td><strong>${calculateBundleCost().toFixed(2)}</strong></td>
                        <td colSpan="2"></td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <div className="empty-state">
                    <p>No components added yet. Add products from the IT menu or custom off-menu items.</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="empty-state">
              <p>Select a bundle to view details and manage components.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Venues Component
function Venues() {
  const [venues, setVenues] = useState([]);
  const [summary, setSummary] = useState([]);
  const [filter, setFilter] = useState({ country: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter.country ? `?country=${filter.country}` : '';
      const [venuesRes, summaryRes] = await Promise.all([
        axios.get(`${API_URL}/venues${params}`),
        axios.get(`${API_URL}/venues/summary`)
      ]);
      setVenues(venuesRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  };

  return (
    <div className="venues">
      <h1>Venues</h1>

      <div className="filters">
        <select
          value={filter.country}
          onChange={e => setFilter({ country: e.target.value })}
        >
          <option value="">All Countries</option>
          <option value="USA">USA</option>
          <option value="MEX">Mexico</option>
          <option value="CAN">Canada</option>
        </select>
      </div>

      <div className="section">
        <h2>Summary by Country & Type</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Country</th>
              <th>Venue Type</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            {summary.map((row, i) => (
              <tr key={i}>
                <td>{row.country}</td>
                <td>{row.venue_type}</td>
                <td>{row.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="section">
        <h2>Venue List ({venues.length})</h2>
        <table className="data-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Venue Name</th>
              <th>Facility</th>
              <th>City</th>
              <th>Country</th>
              <th>Type</th>
            </tr>
          </thead>
          <tbody>
            {venues.slice(0, 50).map(venue => (
              <tr key={venue.id}>
                <td>{venue.teap_venue_code}</td>
                <td>{venue.venue_name}</td>
                <td>{venue.facility_name}</td>
                <td>{venue.city_name}</td>
                <td>{venue.country}</td>
                <td>{venue.venue_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {venues.length > 50 && <p className="note">Showing first 50 of {venues.length} venues</p>}
      </div>
    </div>
  );
}

// Products Component
function Products() {
  const [products, setProducts] = useState([]);
  const [types, setTypes] = useState([]);
  const [filter, setFilter] = useState({ product_type: '' });

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const params = filter.product_type ? `?product_type=${filter.product_type}` : '';
      const [productsRes, typesRes] = await Promise.all([
        axios.get(`${API_URL}/products${params}`),
        axios.get(`${API_URL}/products/types`)
      ]);
      setProducts(productsRes.data);
      setTypes(typesRes.data);
    } catch (err) {
      console.error('Error fetching products:', err);
    }
  };

  return (
    <div className="products">
      <h1>Products (IT Menu)</h1>

      <div className="filters">
        <select
          value={filter.product_type}
          onChange={e => setFilter({ product_type: e.target.value })}
        >
          <option value="">All Types</option>
          {types.map(t => (
            <option key={t.product_type} value={t.product_type}>
              {t.product_type} ({t.count})
            </option>
          ))}
        </select>
      </div>

      <table className="data-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Product Name</th>
            <th>Type</th>
          </tr>
        </thead>
        <tbody>
          {products.map(product => (
            <tr key={product.id}>
              <td>{product.product_code}</td>
              <td>{product.product_name}</td>
              <td>{product.product_type}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Template Builder Component - Configure bundles by location type
function TemplateBuilder() {
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedLocationType, setSelectedLocationType] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [costSummary, setCostSummary] = useState(null);
  const [expandedItems, setExpandedItems] = useState([]);
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [showExpandedView, setShowExpandedView] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  const [countryFilter, setCountryFilter] = useState('');

  const [user] = useState(() => {
    const saved = localStorage.getItem('teap_user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    fetchLocationTypes();
    fetchBundles();
    fetchVersions();
  }, []);

  useEffect(() => {
    if (currentVersion && !currentVersion.is_baseline) {
      fetchTemplates();
      fetchCostSummary();
    }
  }, [currentVersion]);

  const fetchLocationTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/templates/location-types`);
      setLocationTypes(res.data);
    } catch (err) {
      console.error('Error fetching location types:', err);
    }
  };

  const fetchBundles = async () => {
    try {
      const res = await axios.get(`${API_URL}/bundles`);
      setBundles(res.data);
    } catch (err) {
      console.error('Error fetching bundles:', err);
    }
  };

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data);
      const current = res.data.find(v => v.is_current && !v.is_baseline);
      if (current) {
        setCurrentVersion(current);
      } else if (res.data.length > 0) {
        setCurrentVersion(res.data[0]);
      }
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const fetchTemplates = async () => {
    if (!currentVersion) return;
    try {
      const res = await axios.get(`${API_URL}/templates/grouped?version_id=${currentVersion.id}`);
      setTemplates(res.data);
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  const fetchCostSummary = async () => {
    if (!currentVersion) return;
    try {
      const res = await axios.get(`${API_URL}/templates/cost-summary?version_id=${currentVersion.id}`);
      setCostSummary(res.data);
    } catch (err) {
      console.error('Error fetching cost summary:', err);
    }
  };

  const fetchExpandedItems = async () => {
    if (!currentVersion) return;
    try {
      const res = await axios.get(`${API_URL}/templates/expand?version_id=${currentVersion.id}`);
      setExpandedItems(res.data);
      setShowExpandedView(true);
    } catch (err) {
      console.error('Error fetching expanded items:', err);
    }
  };

  const createNewVersion = async () => {
    if (!newVersionName) {
      alert('Please enter a version name');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/versions`, {
        version_name: newVersionName,
        description: 'Template-based TEAP configuration',
        created_by: user?.email || 'unknown'
      });
      setShowCreateVersion(false);
      setNewVersionName('');
      fetchVersions();
      setCurrentVersion(res.data);
    } catch (err) {
      console.error('Error creating version:', err);
    }
  };

  const addBundleToTemplate = async (bundleId, quantity = 1) => {
    if (!selectedLocationType || !currentVersion || currentVersion.is_baseline) return;
    try {
      await axios.post(`${API_URL}/templates`, {
        version_id: currentVersion.id,
        country: selectedLocationType.country,
        venue_type: selectedLocationType.venue_type,
        bundle_id: bundleId,
        quantity
      });
      fetchTemplates();
      fetchCostSummary();
    } catch (err) {
      console.error('Error adding bundle:', err);
    }
  };

  const removeTemplate = async (templateId) => {
    try {
      await axios.delete(`${API_URL}/templates/${templateId}`);
      fetchTemplates();
      fetchCostSummary();
    } catch (err) {
      console.error('Error removing template:', err);
    }
  };

  const updateTemplateQuantity = async (templateId, quantity) => {
    try {
      await axios.put(`${API_URL}/templates/${templateId}`, { quantity });
      fetchTemplates();
      fetchCostSummary();
    } catch (err) {
      console.error('Error updating quantity:', err);
    }
  };

  const getTemplateForLocationType = (country, venueType) => {
    return templates.find(t => t.country === country && t.venue_type === venueType);
  };

  const filteredLocationTypes = countryFilter
    ? locationTypes.filter(lt => lt.country === countryFilter)
    : locationTypes;

  // Group location types by country for display
  const groupedByCountry = {};
  filteredLocationTypes.forEach(lt => {
    if (!groupedByCountry[lt.country]) {
      groupedByCountry[lt.country] = [];
    }
    groupedByCountry[lt.country].push(lt);
  });

  return (
    <div className="template-builder">
      <div className="page-header">
        <h1>Template Builder</h1>
        <div className="version-selector">
          <span>Version: </span>
          <select
            value={currentVersion?.id || ''}
            onChange={(e) => {
              const v = versions.find(v => v.id === parseInt(e.target.value));
              setCurrentVersion(v);
            }}
          >
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.version_name} {v.is_baseline ? '(Baseline - Read Only)' : ''}
              </option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => setShowCreateVersion(true)}>
            + New Version
          </button>
        </div>
      </div>

      {currentVersion?.is_baseline && (
        <div className="warning-banner">
          You're viewing the baseline version (read-only). Create a new version to configure templates.
        </div>
      )}

      {showCreateVersion && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Create New Version</h3>
            <p>Create a new TEAP version to configure bundle templates by location type.</p>
            <input
              type="text"
              placeholder="Version name (e.g., TPT Bundle Config v1)"
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              autoFocus
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateVersion(false)}>Cancel</button>
              <button className="btn-primary" onClick={createNewVersion}>Create</button>
            </div>
          </div>
        </div>
      )}

      <div className="builder-layout">
        {/* Left: Location Types */}
        <div className="builder-sidebar">
          <div className="sidebar-header">
            <h3>Location Types</h3>
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
              <option value="">All Countries</option>
              <option value="USA">USA</option>
              <option value="MEX">Mexico</option>
              <option value="CAN">Canada</option>
            </select>
          </div>

          <div className="location-type-list">
            {Object.entries(groupedByCountry).map(([country, types]) => (
              <div key={country} className="country-group">
                <div className="country-header">{country}</div>
                {types.map(lt => {
                  const template = getTemplateForLocationType(lt.country, lt.venue_type);
                  const isSelected = selectedLocationType?.country === lt.country &&
                                    selectedLocationType?.venue_type === lt.venue_type;
                  return (
                    <div
                      key={`${lt.country}-${lt.venue_type}`}
                      className={`location-type-item ${isSelected ? 'selected' : ''} ${template ? 'configured' : ''}`}
                      onClick={() => setSelectedLocationType(lt)}
                    >
                      <div className="lt-header">
                        <span className="lt-name">{lt.venue_type}</span>
                        <span className="lt-count">{lt.venue_count} venues</span>
                      </div>
                      {template && (
                        <div className="lt-bundles">
                          {template.bundles.length} bundle{template.bundles.length !== 1 ? 's' : ''} assigned
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Middle: Configuration */}
        <div className="builder-main">
          {selectedLocationType ? (
            <>
              <div className="config-header">
                <div>
                  <h2>{selectedLocationType.country} - {selectedLocationType.venue_type}</h2>
                  <p className="config-subtitle">
                    Configure bundles for all {selectedLocationType.venue_count} venues of this type.
                    Total baseline items: {parseInt(selectedLocationType.total_items).toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Current Template Config */}
              <div className="config-section">
                <h3>Assigned Bundles</h3>
                {(() => {
                  const template = getTemplateForLocationType(
                    selectedLocationType.country,
                    selectedLocationType.venue_type
                  );
                  if (!template || template.bundles.length === 0) {
                    return (
                      <div className="empty-state small">
                        <p>No bundles assigned yet. Add bundles below.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="assigned-bundles">
                      {template.bundles.map(b => (
                        <div key={b.id} className="assigned-bundle-card">
                          <div className="bundle-info">
                            <strong>{b.bundle_name}</strong>
                            <span className="bundle-type">{b.bundle_type}</span>
                          </div>
                          <div className="bundle-components">
                            {b.components?.map((c, i) => (
                              <span key={i} className="component-tag">{c.product_name}</span>
                            ))}
                          </div>
                          <div className="bundle-controls">
                            <label>Qty per venue:</label>
                            <input
                              type="number"
                              min="1"
                              value={b.quantity}
                              onChange={(e) => updateTemplateQuantity(b.id, parseInt(e.target.value) || 1)}
                              disabled={currentVersion?.is_baseline}
                            />
                            {!currentVersion?.is_baseline && (
                              <button className="btn-small btn-danger" onClick={() => removeTemplate(b.id)}>
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Add Bundle */}
              {!currentVersion?.is_baseline && bundles.length > 0 && (
                <div className="config-section">
                  <h3>Add Bundle</h3>
                  <div className="bundle-picker">
                    {bundles.map(bundle => {
                      const template = getTemplateForLocationType(
                        selectedLocationType.country,
                        selectedLocationType.venue_type
                      );
                      const alreadyAdded = template?.bundles.some(b => b.bundle_id === bundle.id);
                      return (
                        <button
                          key={bundle.id}
                          className={`btn-bundle-pick ${alreadyAdded ? 'added' : ''}`}
                          onClick={() => !alreadyAdded && addBundleToTemplate(bundle.id)}
                          disabled={alreadyAdded}
                        >
                          <span className="bundle-name">{bundle.bundle_name}</span>
                          <span className="bundle-meta">{bundle.component_count} items • {bundle.bundle_type}</span>
                          {alreadyAdded && <span className="added-badge">Added</span>}
                        </button>
                      );
                    })}
                  </div>
                  {bundles.length === 0 && (
                    <p className="note">No bundles created yet. Go to Bundles page to create some.</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <h2>Select a Location Type</h2>
              <p>Choose a country + venue type combination from the left to configure its bundle template.</p>
              <p className="note">Each location type (e.g., USA Depots) can have different bundles assigned.</p>
            </div>
          )}
        </div>

        {/* Right: Cost Summary */}
        <div className="builder-summary">
          <div className="summary-card">
            <h3>Cost Summary</h3>
            {costSummary && costSummary.details.length > 0 ? (
              <>
                <div className="cost-breakdown">
                  {costSummary.details.map((row, i) => (
                    <div key={i} className="cost-row">
                      <div className="cost-label">
                        <span className="country-badge">{row.country}</span>
                        {row.venue_type}
                        <span className="venue-count">({row.venue_count} venues)</span>
                      </div>
                      <div className="cost-value">${parseFloat(row.total_cost || 0).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div className="cost-totals">
                  <div className="by-country">
                    {Object.entries(costSummary.by_country).map(([country, total]) => (
                      <div key={country} className="country-total">
                        <span>{country}</span>
                        <span>${total.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                  <div className="grand-total">
                    <span>Grand Total</span>
                    <span>${costSummary.grand_total.toLocaleString()}</span>
                  </div>
                </div>
              </>
            ) : (
              <p className="no-data">No templates configured yet</p>
            )}
          </div>

          <button
            className="btn-primary btn-expand"
            onClick={fetchExpandedItems}
            disabled={!currentVersion || currentVersion.is_baseline || !costSummary?.details.length}
          >
            View Expanded Item List
          </button>
        </div>
      </div>

      {/* Expanded Items Modal */}
      {showExpandedView && (
        <div className="modal-overlay" onClick={() => setShowExpandedView(false)}>
          <div className="modal expanded-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Expanded Item List</h3>
              <button className="btn-close" onClick={() => setShowExpandedView(false)}>×</button>
            </div>
            <p className="modal-subtitle">
              This shows every item at every venue based on your template configuration.
              Total: {expandedItems.length} line items
            </p>
            <div className="expanded-table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Venue Type</th>
                    <th>Venue</th>
                    <th>Bundle</th>
                    <th>Product</th>
                    <th>Qty</th>
                    <th>Unit Cost</th>
                    <th>Line Total</th>
                  </tr>
                </thead>
                <tbody>
                  {expandedItems.slice(0, 200).map((item, i) => (
                    <tr key={i}>
                      <td>{item.country}</td>
                      <td>{item.venue_type}</td>
                      <td>{item.venue_name}</td>
                      <td>{item.bundle_name}</td>
                      <td>{item.product_name}</td>
                      <td>{item.quantity}</td>
                      <td>${parseFloat(item.unit_cost || 0).toFixed(2)}</td>
                      <td>${parseFloat(item.line_cost || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {expandedItems.length > 200 && (
                <p className="note">Showing first 200 of {expandedItems.length} items</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Location Planner Component
function LocationPlanner() {
  const [venueTypes, setVenueTypes] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedType, setSelectedType] = useState('');
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [venueItems, setVenueItems] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [versions, setVersions] = useState([]);
  const [currentVersion, setCurrentVersion] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [countryFilter, setCountryFilter] = useState('');
  const [showCreateVersion, setShowCreateVersion] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');

  const [user] = useState(() => {
    const saved = localStorage.getItem('teap_user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    fetchVenueTypes();
    fetchBundles();
    fetchVersions();
  }, []);

  useEffect(() => {
    if (selectedType || countryFilter) {
      fetchVenues();
    }
  }, [selectedType, countryFilter]);

  useEffect(() => {
    if (currentVersion) {
      fetchAssignments();
    }
  }, [currentVersion]);

  const fetchVenueTypes = async () => {
    try {
      const res = await axios.get(`${API_URL}/locations/venue-types`);
      setVenueTypes(res.data);
    } catch (err) {
      console.error('Error fetching venue types:', err);
    }
  };

  const fetchVenues = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedType) params.append('venue_type', selectedType);
      if (countryFilter) params.append('country', countryFilter);
      const res = await axios.get(`${API_URL}/locations/venues?${params}`);
      setVenues(res.data);
    } catch (err) {
      console.error('Error fetching venues:', err);
    }
  };

  const fetchVenueItems = async (venueName) => {
    try {
      const res = await axios.get(`${API_URL}/locations/items?venue_name=${encodeURIComponent(venueName)}`);
      setVenueItems(res.data);
    } catch (err) {
      console.error('Error fetching venue items:', err);
    }
  };

  const fetchBundles = async () => {
    try {
      const res = await axios.get(`${API_URL}/bundles`);
      setBundles(res.data);
    } catch (err) {
      console.error('Error fetching bundles:', err);
    }
  };

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data);
      // Set current version (non-baseline) or create mode
      const current = res.data.find(v => v.is_current && !v.is_baseline);
      const baseline = res.data.find(v => v.is_baseline);
      setCurrentVersion(current || baseline);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const fetchAssignments = async () => {
    if (!currentVersion) return;
    try {
      const res = await axios.get(`${API_URL}/locations/assignments?version_id=${currentVersion.id}`);
      setAssignments(res.data);
    } catch (err) {
      console.error('Error fetching assignments:', err);
    }
  };

  const selectVenue = (venue) => {
    setSelectedVenue(venue);
    fetchVenueItems(venue.venue_name);
  };

  const assignBundle = async (bundleId) => {
    if (!selectedVenue || !currentVersion || currentVersion.is_baseline) {
      alert('Please create a new version first before assigning bundles');
      return;
    }

    try {
      await axios.post(`${API_URL}/locations/assignments`, {
        version_id: currentVersion.id,
        venue_name: selectedVenue.venue_name,
        space_name: null, // Assign to venue level
        country: selectedVenue.country,
        venue_type: selectedVenue.venue_type,
        bundle_id: bundleId,
        quantity: 1
      });
      fetchAssignments();
    } catch (err) {
      console.error('Error assigning bundle:', err);
    }
  };

  const removeAssignment = async (assignmentId) => {
    try {
      await axios.delete(`${API_URL}/locations/assignments/${assignmentId}`);
      fetchAssignments();
    } catch (err) {
      console.error('Error removing assignment:', err);
    }
  };

  const createNewVersion = async () => {
    if (!newVersionName) {
      alert('Please enter a version name');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/versions`, {
        version_name: newVersionName,
        description: 'New version with bundle-based assignments',
        created_by: user?.email || 'unknown'
      });
      setShowCreateVersion(false);
      setNewVersionName('');
      fetchVersions();
      setCurrentVersion(res.data);
    } catch (err) {
      console.error('Error creating version:', err);
    }
  };

  const getVenueAssignments = (venueName) => {
    return assignments.filter(a => a.venue_name === venueName);
  };

  // Group baseline items by type for summary
  const groupItemsByType = (items) => {
    const grouped = {};
    items.forEach(item => {
      const type = item.product_type || 'Other';
      if (!grouped[type]) {
        grouped[type] = { count: 0, quantity: 0 };
      }
      grouped[type].count++;
      grouped[type].quantity += item.quantity || 0;
    });
    return grouped;
  };

  return (
    <div className="location-planner">
      <div className="page-header">
        <h1>Location Planner</h1>
        <div className="version-selector">
          <span>Working Version: </span>
          <select
            value={currentVersion?.id || ''}
            onChange={(e) => {
              const v = versions.find(v => v.id === parseInt(e.target.value));
              setCurrentVersion(v);
            }}
          >
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.version_name} {v.is_baseline ? '(Baseline)' : ''}
              </option>
            ))}
          </select>
          <button className="btn-primary" onClick={() => setShowCreateVersion(true)}>
            + New Version
          </button>
        </div>
      </div>

      {showCreateVersion && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Create New Version</h3>
            <p>Create a new TEAP version to assign bundles and modify the equipment list.</p>
            <input
              type="text"
              placeholder="Version name (e.g., Revised with Bundles v2)"
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              autoFocus
            />
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateVersion(false)}>Cancel</button>
              <button className="btn-primary" onClick={createNewVersion}>Create</button>
            </div>
          </div>
        </div>
      )}

      <div className="planner-layout">
        {/* Left: Filters and Venue List */}
        <div className="planner-sidebar">
          <div className="filters">
            <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)}>
              <option value="">All Countries</option>
              <option value="USA">USA</option>
              <option value="MEX">Mexico</option>
              <option value="CAN">Canada</option>
            </select>
            <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
              <option value="">All Venue Types</option>
              {venueTypes.map(t => (
                <option key={t.venue_type} value={t.venue_type}>
                  {t.venue_type} ({t.venue_count})
                </option>
              ))}
            </select>
          </div>

          <div className="venue-list">
            <h3>Venues ({venues.length})</h3>
            {venues.map(venue => {
              const venueAssignments = getVenueAssignments(venue.venue_name);
              return (
                <div
                  key={`${venue.country}-${venue.venue_name}`}
                  className={`venue-item ${selectedVenue?.venue_name === venue.venue_name ? 'selected' : ''} ${venueAssignments.length > 0 ? 'has-assignments' : ''}`}
                  onClick={() => selectVenue(venue)}
                >
                  <div className="venue-header">
                    <strong>{venue.venue_name}</strong>
                    <span className="venue-country">{venue.country}</span>
                  </div>
                  <div className="venue-meta">
                    <span>{venue.venue_type}</span>
                    <span>{venue.total_items} baseline items</span>
                    {venueAssignments.length > 0 && (
                      <span className="badge assigned">{venueAssignments.length} bundles</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Right: Venue Details */}
        <div className="planner-main">
          {selectedVenue ? (
            <>
              <div className="venue-detail-header">
                <div>
                  <h2>{selectedVenue.venue_name}</h2>
                  <span className="venue-info">{selectedVenue.country} • {selectedVenue.venue_type}</span>
                </div>
              </div>

              {/* Current Assignments for this version */}
              <div className="assignments-section">
                <h3>Assigned Bundles {currentVersion?.is_baseline ? '(View Only - Create new version to edit)' : ''}</h3>
                {getVenueAssignments(selectedVenue.venue_name).length > 0 ? (
                  <div className="assignment-list">
                    {getVenueAssignments(selectedVenue.venue_name).map(assignment => (
                      <div key={assignment.id} className="assignment-card">
                        <div className="assignment-header">
                          <strong>{assignment.bundle_name}</strong>
                          <span className="bundle-type">{assignment.bundle_type}</span>
                          {!currentVersion?.is_baseline && (
                            <button className="btn-small btn-danger" onClick={() => removeAssignment(assignment.id)}>Remove</button>
                          )}
                        </div>
                        {assignment.bundle_items && (
                          <div className="assignment-items">
                            {assignment.bundle_items.map((item, idx) => (
                              <span key={idx} className="item-tag">
                                {item.product_name} x{item.quantity}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state small">
                    <p>No bundles assigned to this venue yet.</p>
                  </div>
                )}

                {/* Add Bundle */}
                {!currentVersion?.is_baseline && bundles.length > 0 && (
                  <div className="add-bundle">
                    <h4>Assign Bundle</h4>
                    <div className="bundle-options">
                      {bundles.map(bundle => (
                        <button
                          key={bundle.id}
                          className="btn-bundle"
                          onClick={() => assignBundle(bundle.id)}
                        >
                          + {bundle.bundle_name}
                          <span className="component-count">{bundle.component_count} items</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Baseline Items */}
              <div className="baseline-section">
                <h3>Baseline TEAP Items (Original Submission)</h3>
                <p className="section-note">
                  These are the items from the original IT submission. Assign bundles above to create your revised version.
                </p>

                {/* Summary by type */}
                <div className="item-summary">
                  {Object.entries(groupItemsByType(venueItems)).map(([type, data]) => (
                    <div key={type} className="summary-chip">
                      <span className="type">{type}</span>
                      <span className="qty">{data.quantity}</span>
                    </div>
                  ))}
                </div>

                <table className="data-table baseline-table">
                  <thead>
                    <tr>
                      <th>Space</th>
                      <th>Product</th>
                      <th>Type</th>
                      <th>Qty</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venueItems.map(item => (
                      <tr key={item.id}>
                        <td>{item.space_name}</td>
                        <td>{item.product_name}</td>
                        <td>{item.product_type}</td>
                        <td>{item.quantity}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="empty-state">
              <h2>Select a Venue</h2>
              <p>Choose a venue from the list to view baseline items and assign bundles.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Versions Component
function Versions() {
  const [versions, setVersions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [newVersion, setNewVersion] = useState({ version_name: '', description: '' });
  const [user] = useState(() => {
    const saved = localStorage.getItem('teap_user');
    return saved ? JSON.parse(saved) : null;
  });

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const createVersion = async () => {
    if (!newVersion.version_name) {
      alert('Please enter a version name');
      return;
    }
    try {
      await axios.post(`${API_URL}/versions`, {
        ...newVersion,
        created_by: user?.email || 'unknown'
      });
      setShowForm(false);
      setNewVersion({ version_name: '', description: '' });
      fetchVersions();
    } catch (err) {
      console.error('Error creating version:', err);
      alert('Error creating version');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="versions">
      <div className="page-header">
        <h1>TEAP Versions</h1>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ Create New Version'}
        </button>
      </div>

      <div className="version-info">
        <p>Track revisions to your TEAP submission. Create a new version when you've made significant changes.</p>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Create New Version</h3>
          <div className="form-group">
            <label>Version Name</label>
            <input
              type="text"
              value={newVersion.version_name}
              onChange={e => setNewVersion({ ...newVersion, version_name: e.target.value })}
              placeholder="e.g., Post-Bundle Review v2.0"
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={newVersion.description}
              onChange={e => setNewVersion({ ...newVersion, description: e.target.value })}
              placeholder="What changes were made in this version?"
            />
          </div>
          <button className="btn-primary" onClick={createVersion}>Create Version</button>
        </div>
      )}

      <div className="versions-timeline">
        {versions.map((version, index) => (
          <div key={version.id} className={`version-card ${version.is_current ? 'current' : ''} ${version.is_baseline ? 'baseline' : ''}`}>
            <div className="version-marker">
              <span className="version-number">v{version.version_number}</span>
            </div>
            <div className="version-content">
              <div className="version-header">
                <h3>{version.version_name}</h3>
                <div className="version-tags">
                  {version.is_baseline && <span className="tag baseline">Baseline</span>}
                  {version.is_current && <span className="tag current">Current</span>}
                </div>
              </div>
              <p className="version-description">{version.description}</p>
              <div className="version-meta">
                <span>Created by {version.created_by}</span>
                <span>{formatDate(version.created_at)}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Exports Component
function Exports() {
  const [versions, setVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportData, setExportData] = useState(null);
  const [exportType, setExportType] = useState(null);

  useEffect(() => {
    fetchVersions();
  }, []);

  useEffect(() => {
    if (selectedVersion && !selectedVersion.is_baseline) {
      fetchComparison();
    }
  }, [selectedVersion]);

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data);
      const current = res.data.find(v => v.is_current && !v.is_baseline);
      if (current) setSelectedVersion(current);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const fetchComparison = async () => {
    if (!selectedVersion) return;
    try {
      const res = await axios.get(`${API_URL}/exports/comparison?version_id=${selectedVersion.id}`);
      setComparison(res.data);
    } catch (err) {
      console.error('Error fetching comparison:', err);
    }
  };

  const generateExport = async (type) => {
    if (!selectedVersion) return;
    setLoading(true);
    setExportType(type);
    try {
      let endpoint;
      switch (type) {
        case 'teap': endpoint = 'teap'; break;
        case 'full': endpoint = 'full-order'; break;
        case 'depot': endpoint = 'depot-manager'; break;
        default: return;
      }
      const res = await axios.get(`${API_URL}/exports/${endpoint}?version_id=${selectedVersion.id}`);
      setExportData(res.data);
    } catch (err) {
      console.error('Error generating export:', err);
      alert('Error generating export');
    }
    setLoading(false);
  };

  const downloadFile = (data, filename, type) => {
    let content;
    let mimeType;

    if (type === 'json') {
      content = JSON.stringify(data, null, 2);
      mimeType = 'application/json';
    } else if (type === 'csv') {
      // Convert to CSV
      const items = data.items || [];
      if (items.length === 0) {
        alert('No data to export');
        return;
      }
      const headers = Object.keys(items[0]);
      const rows = [headers.join(',')];
      items.forEach(item => {
        rows.push(headers.map(h => {
          let val = item[h];
          if (typeof val === 'string' && val.includes(',')) {
            val = `"${val}"`;
          }
          return val ?? '';
        }).join(','));
      });
      content = rows.join('\n');
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getExportTitle = () => {
    switch (exportType) {
      case 'teap': return 'TEAP IT Export';
      case 'full': return 'Full Order Tracking';
      case 'depot': return 'Depot Manager Export';
      default: return 'Export';
    }
  };

  return (
    <div className="exports">
      <div className="page-header">
        <h1>Export Data</h1>
        <div className="version-selector">
          <span>Version: </span>
          <select
            value={selectedVersion?.id || ''}
            onChange={(e) => {
              const v = versions.find(v => v.id === parseInt(e.target.value));
              setSelectedVersion(v);
              setExportData(null);
            }}
          >
            {versions.filter(v => !v.is_baseline).map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.version_name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!selectedVersion || selectedVersion.is_baseline ? (
        <div className="warning-banner">
          Select a configured version to generate exports. Baseline versions cannot be exported.
        </div>
      ) : (
        <>
          {/* Comparison Summary */}
          {comparison && (
            <div className="comparison-section">
              <h2>Baseline vs Configured Comparison</h2>
              <table className="data-table comparison-table">
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Venue Type</th>
                    <th>Venues</th>
                    <th>Baseline Items</th>
                    <th>Configured Items</th>
                    <th>Difference</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.comparison.map((row, i) => (
                    <tr key={i} className={row.difference !== 0 ? (row.difference > 0 ? 'increase' : 'decrease') : ''}>
                      <td>{row.country}</td>
                      <td>{row.venue_type}</td>
                      <td>{row.venue_count}</td>
                      <td>{row.baseline_items.toLocaleString()}</td>
                      <td>{row.configured_items.toLocaleString()}</td>
                      <td className={`diff ${row.difference > 0 ? 'positive' : row.difference < 0 ? 'negative' : ''}`}>
                        {row.difference > 0 ? '+' : ''}{row.difference.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Export Options */}
          <div className="export-options">
            <div className="export-card" onClick={() => generateExport('teap')}>
              <div className="export-icon">IT</div>
              <h3>TEAP IT Export</h3>
              <p>IT-managed items only in the format required for IT submission.</p>
              <span className="export-format">CSV / Excel</span>
            </div>

            <div className="export-card" onClick={() => generateExport('full')}>
              <div className="export-icon">$</div>
              <h3>Full Order Tracking</h3>
              <p>All items (IT + non-IT) with pricing breakdown and cost totals.</p>
              <span className="export-format">CSV / Excel</span>
            </div>

            <div className="export-card" onClick={() => generateExport('depot')}>
              <div className="export-icon">{'{}'}</div>
              <h3>Depot Manager</h3>
              <p>JSON format for consumption by the Depot Manager application.</p>
              <span className="export-format">JSON</span>
            </div>
          </div>

          {/* Export Preview */}
          {loading && (
            <div className="loading">Generating export...</div>
          )}

          {exportData && !loading && (
            <div className="export-preview">
              <div className="preview-header">
                <h2>{getExportTitle()} Preview</h2>
                <div className="preview-actions">
                  {exportType !== 'depot' && (
                    <button
                      className="btn-primary"
                      onClick={() => downloadFile(exportData, `teap_export_${exportType}_${Date.now()}.csv`, 'csv')}
                    >
                      Download CSV
                    </button>
                  )}
                  <button
                    className="btn-secondary"
                    onClick={() => downloadFile(exportData, `teap_export_${exportType}_${Date.now()}.json`, 'json')}
                  >
                    Download JSON
                  </button>
                </div>
              </div>

              <div className="preview-meta">
                <span>Generated: {new Date(exportData.generated_at).toLocaleString()}</span>
                <span>Items: {exportData.item_count || exportData.venue_count || 0}</span>
                {exportData.totals && (
                  <span className="total-cost">Total Cost: ${exportData.totals.grand_total?.toLocaleString()}</span>
                )}
              </div>

              {exportType === 'depot' ? (
                <div className="json-preview">
                  <pre>{JSON.stringify(exportData, null, 2).slice(0, 5000)}
                    {JSON.stringify(exportData).length > 5000 && '\n... (truncated)'}
                  </pre>
                </div>
              ) : (
                <div className="table-preview">
                  <table className="data-table">
                    <thead>
                      <tr>
                        {exportData.items && exportData.items[0] &&
                          Object.keys(exportData.items[0]).slice(0, 8).map(key => (
                            <th key={key}>{key.replace(/_/g, ' ')}</th>
                          ))
                        }
                      </tr>
                    </thead>
                    <tbody>
                      {exportData.items?.slice(0, 20).map((item, i) => (
                        <tr key={i}>
                          {Object.values(item).slice(0, 8).map((val, j) => (
                            <td key={j}>
                              {typeof val === 'boolean' ? (val ? 'Yes' : 'No') :
                               typeof val === 'number' ? val.toLocaleString() : val}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {exportData.items?.length > 20 && (
                    <p className="note">Showing first 20 of {exportData.items.length} items</p>
                  )}
                </div>
              )}

              {/* Cost Breakdown for Full Order */}
              {exportData.totals && (
                <div className="cost-breakdown-section">
                  <h3>Cost Breakdown</h3>
                  <div className="breakdown-grid">
                    <div className="breakdown-card">
                      <h4>By Country</h4>
                      {Object.entries(exportData.totals.by_country).map(([country, cost]) => (
                        <div key={country} className="breakdown-row">
                          <span>{country}</span>
                          <span>${cost.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="breakdown-card">
                      <h4>By Bundle Type</h4>
                      {Object.entries(exportData.totals.by_bundle_type).map(([type, cost]) => (
                        <div key={type} className="breakdown-row">
                          <span>{type}</span>
                          <span>${cost.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Import Component
function ImportData() {
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);

  const handleImport = async (type) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.xlsx';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setLoading(true);
      setStatus(`Importing ${file.name}...`);

      const formData = new FormData();
      formData.append('file', file);

      try {
        const res = await axios.post(`${API_URL}/import/${type}`, formData);
        setStatus(`Import successful! ${JSON.stringify(res.data.imported)}`);
      } catch (err) {
        setStatus(`Import failed: ${err.message}`);
      }
      setLoading(false);
    };
    input.click();
  };

  const handleClear = async () => {
    if (!window.confirm('Clear ALL data? This cannot be undone.')) return;
    setLoading(true);
    try {
      await axios.post(`${API_URL}/import/clear`);
      setStatus('All data cleared');
    } catch (err) {
      setStatus(`Clear failed: ${err.message}`);
    }
    setLoading(false);
  };

  return (
    <div className="import">
      <h1>Import Data</h1>

      <div className="import-cards">
        <div className="import-card">
          <h3>TEAP File</h3>
          <p>Import TEAP Excel file with venues, spaces, products, and requests.</p>
          <button
            className="btn-primary"
            onClick={() => handleImport('teap')}
            disabled={loading}
          >
            Import TEAP File
          </button>
        </div>

        <div className="import-card">
          <h3>Pricing File</h3>
          <p>Import pricing Excel file with device and equipment costs.</p>
          <button
            className="btn-primary"
            onClick={() => handleImport('pricing')}
            disabled={loading}
          >
            Import Pricing File
          </button>
        </div>

        <div className="import-card danger">
          <h3>Clear Data</h3>
          <p>Remove all imported data to start fresh.</p>
          <button
            className="btn-danger"
            onClick={handleClear}
            disabled={loading}
          >
            Clear All Data
          </button>
        </div>
      </div>

      {status && (
        <div className="status-message">
          {loading && <span className="spinner"></span>}
          {status}
        </div>
      )}
    </div>
  );
}

// Version Comparison Component
function VersionComparison() {
  const [versions, setVersions] = useState([]);
  const [versionA, setVersionA] = useState(null);
  const [versionB, setVersionB] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data);
      // Default: baseline vs current
      const baseline = res.data.find(v => v.is_baseline);
      const current = res.data.find(v => v.is_current && !v.is_baseline);
      if (baseline) setVersionA(baseline.id);
      if (current) setVersionB(current.id);
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const runComparison = async () => {
    if (!versionA || !versionB) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/compare/${versionA}/${versionB}`);
      setComparison(res.data);
    } catch (err) {
      console.error('Error comparing versions:', err);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (versionA && versionB) {
      runComparison();
    }
  }, [versionA, versionB]);

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  const formatNumber = (val) => {
    return new Intl.NumberFormat('en-US').format(val || 0);
  };

  return (
    <div className="compare">
      <h1>Version Comparison</h1>

      <div className="compare-selectors">
        <div className="selector-group">
          <label>Version A (From)</label>
          <select value={versionA || ''} onChange={e => setVersionA(parseInt(e.target.value))}>
            <option value="">Select version...</option>
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.version_name} {v.is_baseline ? '(Baseline)' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="compare-arrow">→</div>
        <div className="selector-group">
          <label>Version B (To)</label>
          <select value={versionB || ''} onChange={e => setVersionB(parseInt(e.target.value))}>
            <option value="">Select version...</option>
            {versions.map(v => (
              <option key={v.id} value={v.id}>
                v{v.version_number} - {v.version_name} {v.is_current ? '(Current)' : ''}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading && <div className="loading">Calculating comparison...</div>}

      {comparison && !loading && (
        <>
          <div className="compare-summary-cards">
            <div className={`summary-card ${comparison.summary.total_cost_diff < 0 ? 'savings' : 'increase'}`}>
              <h3>Cost Difference</h3>
              <div className="big-number">{formatCurrency(comparison.summary.total_cost_diff)}</div>
              <p>{comparison.summary.savings_percentage > 0 ? `${comparison.summary.savings_percentage}% savings` : `${Math.abs(comparison.summary.savings_percentage)}% increase`}</p>
            </div>
            <div className="summary-card">
              <h3>Version A Total</h3>
              <div className="big-number">{formatCurrency(comparison.summary.total_cost_a)}</div>
              <p>{formatNumber(comparison.summary.total_items_a)} items</p>
            </div>
            <div className="summary-card">
              <h3>Version B Total</h3>
              <div className="big-number">{formatCurrency(comparison.summary.total_cost_b)}</div>
              <p>{formatNumber(comparison.summary.total_items_b)} items</p>
            </div>
            <div className={`summary-card ${comparison.summary.total_items_diff < 0 ? 'savings' : 'increase'}`}>
              <h3>Item Difference</h3>
              <div className="big-number">{formatNumber(comparison.summary.total_items_diff)}</div>
              <p>items {comparison.summary.total_items_diff < 0 ? 'removed' : 'added'}</p>
            </div>
          </div>

          <div className="compare-tabs">
            <button className={activeTab === 'summary' ? 'active' : ''} onClick={() => setActiveTab('summary')}>By Country</button>
            <button className={activeTab === 'venue_type' ? 'active' : ''} onClick={() => setActiveTab('venue_type')}>By Venue Type</button>
            <button className={activeTab === 'changes' ? 'active' : ''} onClick={() => setActiveTab('changes')}>Item Changes ({comparison.total_changes})</button>
          </div>

          {activeTab === 'summary' && (
            <div className="compare-table">
              <table>
                <thead>
                  <tr>
                    <th>Country</th>
                    <th>Cost A</th>
                    <th>Cost B</th>
                    <th>Cost Diff</th>
                    <th>Items A</th>
                    <th>Items B</th>
                    <th>Items Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparison.by_country).map(([country, data]) => (
                    <tr key={country} className={data.cost_diff < 0 ? 'row-savings' : data.cost_diff > 0 ? 'row-increase' : ''}>
                      <td><strong>{country || 'Unknown'}</strong></td>
                      <td>{formatCurrency(data.cost_a)}</td>
                      <td>{formatCurrency(data.cost_b)}</td>
                      <td className={data.cost_diff < 0 ? 'text-success' : 'text-danger'}>{formatCurrency(data.cost_diff)}</td>
                      <td>{formatNumber(data.items_a)}</td>
                      <td>{formatNumber(data.items_b)}</td>
                      <td className={data.items_diff < 0 ? 'text-success' : 'text-danger'}>{formatNumber(data.items_diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'venue_type' && (
            <div className="compare-table">
              <table>
                <thead>
                  <tr>
                    <th>Venue Type</th>
                    <th>Cost A</th>
                    <th>Cost B</th>
                    <th>Cost Diff</th>
                    <th>Items A</th>
                    <th>Items B</th>
                    <th>Items Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(comparison.by_venue_type).map(([venueType, data]) => (
                    <tr key={venueType} className={data.cost_diff < 0 ? 'row-savings' : data.cost_diff > 0 ? 'row-increase' : ''}>
                      <td><strong>{venueType || 'Unknown'}</strong></td>
                      <td>{formatCurrency(data.cost_a)}</td>
                      <td>{formatCurrency(data.cost_b)}</td>
                      <td className={data.cost_diff < 0 ? 'text-success' : 'text-danger'}>{formatCurrency(data.cost_diff)}</td>
                      <td>{formatNumber(data.items_a)}</td>
                      <td>{formatNumber(data.items_b)}</td>
                      <td className={data.items_diff < 0 ? 'text-success' : 'text-danger'}>{formatNumber(data.items_diff)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {activeTab === 'changes' && (
            <div className="compare-table">
              <table>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Country</th>
                    <th>Venue</th>
                    <th>Product</th>
                    <th>Qty Before</th>
                    <th>Qty After</th>
                    <th>Cost Before</th>
                    <th>Cost After</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.item_changes.slice(0, 200).map((change, idx) => (
                    <tr key={idx} className={`change-${change.type}`}>
                      <td>
                        <span className={`change-badge ${change.type}`}>
                          {change.type === 'added' ? '+' : change.type === 'removed' ? '-' : '~'}
                        </span>
                      </td>
                      <td>{change.country}</td>
                      <td>{change.venue_name}</td>
                      <td>{change.product_name}</td>
                      <td>{change.qty_before}</td>
                      <td>{change.qty_after}</td>
                      <td>{formatCurrency(change.cost_before)}</td>
                      <td>{formatCurrency(change.cost_after)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {comparison.item_changes.length > 200 && (
                <p className="more-items">Showing 200 of {comparison.total_changes} changes</p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Procurement Tracker Component
function ProcurementTracker() {
  const [requests, setRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [history, setHistory] = useState([]);
  const [versions, setVersions] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newRequest, setNewRequest] = useState({ version_id: '', request_type: 'teap_it' });

  useEffect(() => {
    fetchRequests();
    fetchVersions();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/procurement`);
      setRequests(res.data);
    } catch (err) {
      console.error('Error fetching procurement requests:', err);
    }
  };

  const fetchVersions = async () => {
    try {
      const res = await axios.get(`${API_URL}/versions`);
      setVersions(res.data.filter(v => !v.is_baseline));
    } catch (err) {
      console.error('Error fetching versions:', err);
    }
  };

  const fetchRequestDetails = async (id) => {
    try {
      const [reqRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/procurement/${id}`),
        axios.get(`${API_URL}/procurement/${id}/history`)
      ]);
      setSelectedRequest(reqRes.data);
      setHistory(histRes.data);
    } catch (err) {
      console.error('Error fetching request details:', err);
    }
  };

  const createRequest = async () => {
    if (!newRequest.version_id) return;
    try {
      // Get expanded items for this version
      const itemsRes = await axios.get(`${API_URL}/templates/expand?version_id=${newRequest.version_id}`);
      const items = itemsRes.data.map(item => ({
        country: item.country,
        venue_type: item.venue_type,
        venue_name: item.venue_name,
        product_name: item.product_name,
        product_code: item.product_code,
        quantity_requested: parseInt(item.quantity),
        unit_cost: parseFloat(item.unit_cost) || 0
      }));

      const user = JSON.parse(localStorage.getItem('teap_user'));
      await axios.post(`${API_URL}/procurement`, {
        version_id: newRequest.version_id,
        request_type: newRequest.request_type,
        requested_by: user?.email || 'unknown',
        items
      });

      setShowCreateModal(false);
      setNewRequest({ version_id: '', request_type: 'teap_it' });
      fetchRequests();
    } catch (err) {
      console.error('Error creating request:', err);
    }
  };

  const updateStatus = async (requestId, newStatus) => {
    const user = JSON.parse(localStorage.getItem('teap_user'));
    try {
      await axios.put(`${API_URL}/procurement/${requestId}/status`, {
        status: newStatus,
        changed_by: user?.email || 'unknown'
      });
      fetchRequests();
      if (selectedRequest) {
        fetchRequestDetails(requestId);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: '#6c757d',
      submitted: '#007bff',
      approved: '#28a745',
      ordered: '#17a2b8',
      partial: '#ffc107',
      complete: '#198754',
      rejected: '#dc3545'
    };
    return colors[status] || '#6c757d';
  };

  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val || 0);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  return (
    <div className="procurement">
      <div className="procurement-header">
        <h1>Procurement Tracker</h1>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + New Request
        </button>
      </div>

      <div className="procurement-layout">
        <div className="requests-list">
          <h3>Requests</h3>
          {requests.length === 0 ? (
            <p className="empty-state">No procurement requests yet. Create one to get started.</p>
          ) : (
            requests.map(req => (
              <div
                key={req.id}
                className={`request-card ${selectedRequest?.id === req.id ? 'selected' : ''}`}
                onClick={() => fetchRequestDetails(req.id)}
              >
                <div className="request-header">
                  <span className="request-type">{req.request_type?.toUpperCase()}</span>
                  <span className="status-badge" style={{ backgroundColor: getStatusColor(req.status) }}>
                    {req.status}
                  </span>
                </div>
                <div className="request-meta">
                  <span>v{req.version_name}</span>
                  <span>{req.item_count} items</span>
                  <span>{formatCurrency(req.total_cost)}</span>
                </div>
                <div className="request-date">{formatDate(req.requested_at)}</div>
              </div>
            ))
          )}
        </div>

        <div className="request-detail">
          {!selectedRequest ? (
            <div className="empty-detail">
              <p>Select a request to view details</p>
            </div>
          ) : (
            <>
              <div className="detail-header">
                <h2>Request #{selectedRequest.id}</h2>
                <div className="status-actions">
                  <span className="current-status" style={{ backgroundColor: getStatusColor(selectedRequest.status) }}>
                    {selectedRequest.status}
                  </span>
                  <select
                    value=""
                    onChange={e => e.target.value && updateStatus(selectedRequest.id, e.target.value)}
                  >
                    <option value="">Change status...</option>
                    <option value="draft">Draft</option>
                    <option value="submitted">Submitted</option>
                    <option value="approved">Approved</option>
                    <option value="ordered">Ordered</option>
                    <option value="partial">Partial</option>
                    <option value="complete">Complete</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              <div className="detail-stats">
                <div className="stat">
                  <label>Type</label>
                  <span>{selectedRequest.request_type}</span>
                </div>
                <div className="stat">
                  <label>Version</label>
                  <span>{selectedRequest.version_name}</span>
                </div>
                <div className="stat">
                  <label>Items</label>
                  <span>{selectedRequest.items?.length || 0}</span>
                </div>
                <div className="stat">
                  <label>Total Cost</label>
                  <span>{formatCurrency(selectedRequest.total_cost)}</span>
                </div>
                <div className="stat">
                  <label>Requested By</label>
                  <span>{selectedRequest.requested_by}</span>
                </div>
              </div>

              <h3>Items</h3>
              <div className="items-table">
                <table>
                  <thead>
                    <tr>
                      <th>Country</th>
                      <th>Venue</th>
                      <th>Product</th>
                      <th>Requested</th>
                      <th>Approved</th>
                      <th>Ordered</th>
                      <th>Received</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedRequest.items?.slice(0, 100).map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.country}</td>
                        <td>{item.venue_name}</td>
                        <td>{item.product_name}</td>
                        <td>{item.quantity_requested}</td>
                        <td>{item.quantity_approved || '-'}</td>
                        <td>{item.quantity_ordered || '-'}</td>
                        <td>{item.quantity_received || '-'}</td>
                        <td>
                          <span className="item-status" style={{ backgroundColor: getStatusColor(item.status) }}>
                            {item.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {selectedRequest.items?.length > 100 && (
                  <p className="more-items">Showing 100 of {selectedRequest.items.length} items</p>
                )}
              </div>

              <h3>History</h3>
              <div className="history-timeline">
                {history.length === 0 ? (
                  <p>No status changes yet</p>
                ) : (
                  history.map((h, idx) => (
                    <div key={idx} className="history-item">
                      <div className="history-time">{formatDate(h.changed_at)}</div>
                      <div className="history-change">
                        <span style={{ color: getStatusColor(h.old_status) }}>{h.old_status}</span>
                        {' → '}
                        <span style={{ color: getStatusColor(h.new_status) }}>{h.new_status}</span>
                      </div>
                      <div className="history-by">by {h.changed_by}</div>
                      {h.notes && <div className="history-notes">{h.notes}</div>}
                    </div>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Create Procurement Request</h2>
            <div className="form-group">
              <label>Version</label>
              <select
                value={newRequest.version_id}
                onChange={e => setNewRequest({ ...newRequest, version_id: e.target.value })}
              >
                <option value="">Select version...</option>
                {versions.map(v => (
                  <option key={v.id} value={v.id}>
                    v{v.version_number} - {v.version_name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Request Type</label>
              <select
                value={newRequest.request_type}
                onChange={e => setNewRequest({ ...newRequest, request_type: e.target.value })}
              >
                <option value="teap_it">TEAP IT (On-Menu Only)</option>
                <option value="full_order">Full Order (All Items)</option>
                <option value="depot">Depot Manager</option>
              </select>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
              <button className="btn-primary" onClick={createRequest} disabled={!newRequest.version_id}>
                Create Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Main App
function App() {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [user, setUser] = useState(() => {
    const saved = localStorage.getItem('teap_user');
    return saved ? JSON.parse(saved) : null;
  });

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('teap_user');
    setUser(null);
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard': return <Dashboard />;
      case 'templates': return <TemplateBuilder />;
      case 'planner': return <LocationPlanner />;
      case 'inventory': return <Inventory />;
      case 'bundles': return <Bundles />;
      case 'compare': return <VersionComparison />;
      case 'procurement': return <ProcurementTracker />;
      case 'exports': return <Exports />;
      case 'versions': return <Versions />;
      case 'venues': return <Venues />;
      case 'products': return <Products />;
      case 'import': return <ImportData />;
      default: return <Dashboard />;
    }
  };

  // Show login if not authenticated
  if (!user) {
    return <Login onLogin={handleLogin} />;
  }

  return (
    <div className="app">
      <Navigation
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        user={user}
        onLogout={handleLogout}
      />
      <main className="main-content">
        {renderPage()}
      </main>
    </div>
  );
}

export default App;
